import axios from 'axios';
import { attach, detach, getConfig } from 'retry-axios';
import forIn from 'lodash/forIn';
import values from 'lodash/values';
import HttpErrorLogger from './httpErrorLogger';

function extractHttpStatusCodesToRetryFromMistransformedRequestConfig(config) {
  let httpStatusCodesToRetry = [];
  if (!(config.statusCodesToRetry instanceof Array)) {
    forIn(
      config.statusCodesToRetry,
      // eslint-disable-next-line no-unused-vars
      (value, key, object) => httpStatusCodesToRetry.push(values(value)),
    );
  } else {
    httpStatusCodesToRetry = config.statusCodesToRetry;
  }
  return httpStatusCodesToRetry;
}

function configureHttpClientToRetry(client, retryConfiguration) {
  // eslint-disable-next-line no-param-reassign
  client.defaults.raxConfig = {
    retry: retryConfiguration.retry,
    noResponseRetries: retryConfiguration.noResponseRetries,
    retryDelay: retryConfiguration.retryDelay,
    instance: client,

    onRetryAttempt: err => {
      const config = getConfig(err);
      console.info(`HTTP connection retry attempt #${config.currentRetryAttempt}`);
    },

    shouldRetry: err => {
      const config = getConfig(err);

      let shouldRetryCurrentError = false;

      if (err.response) {
        const httpStatusCodesToRetry = extractHttpStatusCodesToRetryFromMistransformedRequestConfig(config);

        for (let i = 0; i < httpStatusCodesToRetry.length; i++) {
          const codeRange = httpStatusCodesToRetry[i];
          if (codeRange[0] <= err.response.status && codeRange[1] >= err.response.status) {
            shouldRetryCurrentError = true;
          }
        }
      } else {
        shouldRetryCurrentError = true;
      }

      return config.currentRetryAttempt < retryConfiguration.retry && shouldRetryCurrentError;
    },
  };

  const retryInterceptor = attach(client);
  return retryInterceptor;
}

export default class HttpClientWithRetry {
  /* istanbul ignore next */
  constructor({
    serviceConfiguration: { baseUrl = '', requestTimeoutInMs = 5000 },
    retryConfiguration: { retry = 3, noResponseRetries = 2, retryDelayInMs = 100 } = {},
    httpErrorHandler = new HttpErrorLogger(console),
  }) {
    this.httpClient = axios.create({
      baseURL: baseUrl,
      timeout: requestTimeoutInMs,
      headers: {
        Accept: 'application/json',
      },
    });

    this.retryConfiguration = { retry, noResponseRetries, retryDelayInMs };
    this.errorHandler = httpErrorHandler;
  }

  async post(path, requestBody) {
    return this.execute('post', path, requestBody);
  }

  async put(path, requestBody) {
    return this.execute('put', path, requestBody);
  }

  async head(path, options) {
    return this.execute('head', path, options);
  }

  async get(path, options) {
    return this.execute('get', path, options);
  }

  async patch(path, requestBody) {
    return this.execute('patch', path, requestBody);
  }

  async delete(path) {
    return this.execute('delete', path);
  }

  async execute(method, path, options) {
    const retryInterceptor = configureHttpClientToRetry(this.httpClient, this.retryConfiguration);

    try {
      const response = await this.httpClient[method](path, options);
      return response;
    } catch (error) {
      this.errorHandler.handleError(error);
      throw error;
    } finally {
      detach(retryInterceptor, this.httpClient);
    }
  }

  setHeaders(headers) {
    this.httpClient.defaults.headers = headers;
  }
}
